
import { useState, type ReactNode } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { CreateOrgInputSchema, EMPTY_CREATE_ORG } from './inputs.ts';
import type { Role } from '@shared/user';
import { Button, Field, Stack, Text } from '@client/shared/design';
import { useForm, zodValidator, firstFieldError, TextField } from '@client/shared/form/index.ts';
import { invalidateSession } from '@client/shared/auth/index.ts';
import { RolePicker } from '@client/features/users';
import { postCreateOrganization } from './wizard-api.ts';
import { clearStashedOwnerName } from './owner-name-storage.ts';

const OWNER_LOCKED_ROLES: readonly Role[] = ['owner'];
const ONBOARDING_ROLES: readonly Role[] = ['owner', 'agent'];

export interface CreateOrgStepProps {
  // Always carried from Step 1's onComplete: create-org is unreachable without it (ticket 04).
  readonly ownerName: string;
}

async function submitCreateOrg(
  queryClient: QueryClient,
  input: {
    readonly organizationName: string;
    readonly ownerName: string;
    readonly ownerRoles: readonly Role[];
  },
): Promise<string | null> {
  const outcome = await postCreateOrganization({
    organizationName: input.organizationName.trim(),
    ownerName: input.ownerName.trim(),
    ownerRoles: input.ownerRoles,
  });
  if (outcome.kind === 'error') return outcome.message;
  clearStashedOwnerName();
  await invalidateSession(queryClient);
  return null;
}

function FormChrome({
  onSubmit,
  formError,
  children,
}: {
  readonly onSubmit: () => void;
  readonly formError: string | null;
  readonly children: ReactNode;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      noValidate
    >
      <Stack gap={4}>
        {children}

        {formError && (
          <Text size="sm" tone="danger" role="alert">
            {formError}
          </Text>
        )}
      </Stack>
    </form>
  );
}

function SubmitButton({ isSubmitting }: { readonly isSubmitting: boolean }) {
  return (
    <Button type="submit" size="lg" block loading={isSubmitting}>
      Create group
    </Button>
  );
}

export function CreateOrgStep({ ownerName }: CreateOrgStepProps) {
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: EMPTY_CREATE_ORG,
    validators: { onSubmit: zodValidator(CreateOrgInputSchema) },
    onSubmit: async ({ value }) => {
      setFormError(
        await submitCreateOrg(queryClient, {
          organizationName: value.organizationName,
          ownerName,
          ownerRoles: value.ownerRoles,
        }),
      );
    },
  });

  return (
    <FormChrome onSubmit={() => void form.handleSubmit()} formError={formError}>
      <form.Field name="organizationName">
        {(field) => (
          <TextField
            field={field}
            label="Group name"
            hint="You can change this later in settings."
            type="text"
            autoComplete="organization"
            placeholder="Acme Realty"
          />
        )}
      </form.Field>
      <form.Field name="ownerRoles">
        {(field) => (
          <Field
            label="Your roles"
            error={firstFieldError(field.state.meta.errors)}
          >
            {(wiring) => (
              <RolePicker
                id={wiring.id}
                aria-invalid={wiring['aria-invalid']}
                aria-describedby={wiring['aria-describedby']}
                name={field.name}
                value={field.state.value as Role[]}
                onChange={(roles) => field.handleChange(roles)}
                lockedRoles={OWNER_LOCKED_ROLES}
                roles={ONBOARDING_ROLES}
                stacked
              />
            )}
          </Field>
        )}
      </form.Field>
      <form.Subscribe selector={(s) => s.isSubmitting}>
        {(isSubmitting) => <SubmitButton isSubmitting={isSubmitting} />}
      </form.Subscribe>
    </FormChrome>
  );
}
